import React, { useState, useEffect, useMemo } from 'react';
import {
    MdCalendarMonth, MdClock, MdPerson, MdCheckCircle, MdInfoOutline,
    MdClose, MdWarning, MdDelete, MdExpandMore, MdKeyboardArrowDown,
    MdAdd
} from 'react-icons/md';
import { api } from 'services/api';
import {BsClock} from "react-icons/bs";

// --- SABİTLER ---
const daysOfWeek = [
    { label: 'Pazartesi', value: 'Pazartesi', short: 'Pzt' },
    { label: 'Salı', value: 'Salı', short: 'Sal' },
    { label: 'Çarşamba', value: 'Çarşamba', short: 'Çar' },
    { label: 'Perşembe', value: 'Perşembe', short: 'Per' },
    { label: 'Cuma', value: 'Cuma', short: 'Cum' },
    { label: 'Cumartesi', value: 'Cumartesi', short: 'Cmt' },
    { label: 'Pazar', value: 'Pazar', short: 'Paz' },
];

const generateTimeOptions = () => {
    const options = [];
    for (let i = 8; i < 24; i++) {
        const hour = i.toString().padStart(2, '0');
        options.push(`${hour}:00`, `${hour}:15`, `${hour}:30`, `${hour}:45`);
    }
    return options;
};
const timeOptions = generateTimeOptions();

const slotDurations = [
    { value: 15, label: '15 Dakika' },
    { value: 30, label: '30 Dakika' },
    { value: 45, label: '45 Dakika' },
    { value: 60, label: '60 Dakika' },
    { value: 90, label: '90 Dakika' },
    { value: 120, label: '120 Dakika' },
];

const PAGE_SIZE = 5;

// --- SKELETON LOADERS ---
const MainSkeleton = () => (
    <div className="animate-pulse flex flex-col gap-6 mt-4">
        <div className="h-64 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
        <div className="h-20 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
        <div className="h-20 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
    </div>
);

const WorkDaysSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 animate-pulse">
        {[1, 2, 3].map(i => <div key={i} className="h-28 w-full rounded-2xl bg-gray-100 dark:bg-navy-900/50" />)}
    </div>
);

// --- BİLGİLENDİRME MODALI (Rehber) ---
const InfoModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-navy-900/40 backdrop-blur-sm p-4 transition-all">
            <div className="relative w-full max-w-lg bg-white dark:bg-navy-800 rounded-[24px] shadow-2xl p-6">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100 dark:border-white/10">
                    <h3 className="text-xl font-bold text-navy-700 dark:text-white flex items-center gap-2">
                        <MdInfoOutline className="text-brand-500 text-2xl" /> Sistem Rehberi
                    </h3>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-navy-700 text-gray-500 transition-colors">
                        <MdClose className="text-xl" />
                    </button>
                </div>
                <div className="text-sm font-medium text-gray-600 dark:text-gray-300 space-y-4">
                    <p><strong>Toplu Şablon Oluşturucu</strong> sayesinde aynı anda birden fazla personelin mesai saatlerini dakikalar içinde kurgulayabilirsiniz.</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong className="text-navy-700 dark:text-white">Çakışma Kontrolü:</strong> Aynı personel için aynı güne zaten bir şablon oluşturulmuşsa, sistem o günü atlar ve hata raporlar. Bu yüzden önce eski şablonu silmeniz önerilir.</li>
                        <li><strong className="text-navy-700 dark:text-white">Randevu Aralığı:</strong> Buradaki süre, personelin o gün alacağı minimum hizmet süresi bloklarını belirler (Örn: 30 Dk).</li>
                        <li><strong className="text-navy-700 dark:text-white">Mevcut Şablonları Silme:</strong> Bir şablonu sildiğinizde, o şablona bağlı olan <u>henüz rezerve edilmemiş (boş)</u> tüm randevu slotları da sistemden silinir.</li>
                    </ul>
                </div>
                <div className="mt-6 flex justify-end">
                    <button onClick={onClose} className="px-6 py-2 bg-brand-500 text-white font-bold rounded-xl shadow-lg shadow-brand-500/30 hover:bg-brand-600 transition-colors">
                        Anladım
                    </button>
                </div>
            </div>
        </div>
    );
};


const AdminScheduleManager = () => {
    // --- STATE ---
    const [staffList, setStaffList] = useState([]);

    // Çoklu Seçim State'leri
    const [selectedStaffIds, setSelectedStaffIds] = useState([]);
    const [selectedDays, setSelectedDays] = useState([]);

    // Zaman Ayarları
    const [startTime, setStartTime] = useState('09:00');
    const [endTime, setEndTime] = useState('18:00');
    const [slotDuration, setSlotDuration] = useState(30);

    // UI State
    const [loading, setLoading] = useState(false); // Bulk Buton Loaders
    const [fetchingStaff, setFetchingStaff] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isInfoOpen, setIsInfoOpen] = useState(false);
    const [deletingId, setDeletingId] = useState(null); // Silinen workDay loader için

    // Akordeon & Şablonlar
    const [expandedStaffId, setExpandedStaffId] = useState(null);
    const [staffWorkDays, setStaffWorkDays] = useState([]);
    const [loadingWorkDays, setLoadingWorkDays] = useState(false);

    // Pagination State (Alt Listesi İçin)
    const [currentPage, setCurrentPage] = useState(1);

    // --- BAŞLANGIÇ ---
    useEffect(() => {
        fetchStaffList();
    }, []);

    useEffect(() => {
        if (expandedStaffId) fetchWorkDays(expandedStaffId);
    }, [expandedStaffId]);

    // --- API İŞLEMLERİ ---
    const fetchStaffList = async () => {
        try {
            setFetchingStaff(true);
            const response = await api.get('/staff');
            if (response.success || response.staff) {
                setStaffList(response.staff || []);
            }
        } catch (err) {
            console.error('Personel listesi hatası:', err);
            setMessage({ type: 'danger', text: 'Personel listesi yüklenemedi.' });
        } finally {
            setFetchingStaff(false);
        }
    };

    const fetchWorkDays = async (staffId) => {
        try {
            setLoadingWorkDays(true);
            const response = await api.get(`/staff/${staffId}/work-days`);
            if (response.success || response.work_days) {
                setStaffWorkDays(response.work_days || []);
            }
        } catch (err) {
            console.error('Çalışma günleri hatası:', err);
        } finally {
            setLoadingWorkDays(false);
        }
    };

    // --- ÇOKLU SEÇİM YARDIMCILARI ---
    const toggleStaff = (id) => {
        setSelectedStaffIds(prev => prev.includes(id) ? prev.filter(sId => sId !== id) : [...prev, id]);
    };

    const toggleAllStaff = () => {
        setSelectedStaffIds(selectedStaffIds.length === staffList.length ? [] : staffList.map(s => s.id));
    };

    const toggleDay = (dayValue) => {
        setSelectedDays(prev => prev.includes(dayValue) ? prev.filter(d => d !== dayValue) : [...prev, dayValue]);
    };

    const toggleAllDays = () => {
        const weekdays = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma'];
        const allSelected = weekdays.every(d => selectedDays.includes(d));
        if (allSelected) {
            setSelectedDays(prev => prev.filter(d => !weekdays.includes(d)));
        } else {
            setSelectedDays([...new Set([...selectedDays, ...weekdays])]);
        }
    };

    // --- TOPLU EKLEME İŞLEMİ (LOGIC CORE) ---
    const handleBulkCreate = async () => {
        if (selectedStaffIds.length === 0) return setMessage({ type: 'danger', text: 'Lütfen en az bir personel seçin.' });
        if (selectedDays.length === 0) return setMessage({ type: 'danger', text: 'Lütfen en az bir gün seçin.' });
        if (startTime >= endTime) return setMessage({ type: 'danger', text: 'Başlangıç saati bitiş saatinden küçük olmalıdır.' });

        setLoading(true);
        setMessage({ type: '', text: '' });

        let completed = 0;
        let failures = 0;

        try {
            const promises = [];
            selectedStaffIds.forEach(staffId => {
                selectedDays.forEach(day => {
                    const payload = {
                        staff_id: staffId,
                        day_of_week: day,
                        start_time: startTime,
                        end_time: endTime,
                        slot_duration: slotDuration,
                        is_active: true
                    };
                    promises.push(
                        api.post('/work-days/create-with-slots', payload)
                            .then(res => { if(res.success || res) completed++; })
                            .catch(err => { console.error(`Hata (${staffId}-${day}):`, err); failures++; })
                    );
                });
            });

            await Promise.all(promises);

            if (failures === 0) {
                setMessage({ type: 'success', text: `İşlem Başarılı! ${completed} adet şablon oluşturuldu.` });
                setSelectedDays([]);
                setSelectedStaffIds([]);
            } else {
                setMessage({ type: 'warning', text: `${completed} şablon oluşturuldu fakat ${failures} tanesinde hata/çakışma oluştu.` });
            }

            if (expandedStaffId) fetchWorkDays(expandedStaffId);

        } catch (err) {
            setMessage({ type: 'danger', text: 'Beklenmeyen bir hata oluştu.' });
        } finally {
            setLoading(false);
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        }
    };

    const handleDeleteWorkDay = async (workDayId) => {
        if (!window.confirm('Bu şablonu ve gelecekteki boş randevuları silmek istediğinize emin misiniz?')) return;
        try {
            setDeletingId(workDayId);
            await api.delete(`/work-days/${workDayId}`);
            if (expandedStaffId) fetchWorkDays(expandedStaffId);
        } catch (err) {
            alert('Silme işlemi başarısız: ' + err.message);
        } finally {
            setDeletingId(null);
        }
    };

    // --- SAYFALAMA (PAGINATION) ---
    const totalPages = Math.ceil(staffList.length / PAGE_SIZE);
    const currentStaffList = staffList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    if (fetchingStaff) return <MainSkeleton />;

    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20">

            {/* İNFO MODAL */}
            <InfoModal isOpen={isInfoOpen} onClose={() => setIsInfoOpen(false)} />

            {/* 1. BÖLÜM: TOPLU ŞABLON OLUŞTURUCU KARTI */}
            <div className="flex flex-col bg-white dark:bg-navy-800 rounded-[20px] shadow-3xl shadow-shadow-500 dark:shadow-none border border-gray-100 dark:border-white/10 overflow-hidden">

                {/* Başlık Alanı */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-navy-900/20">
                    <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-full bg-brand-500/10 flex items-center justify-center text-brand-500 dark:bg-brand-400/10 dark:text-brand-400">
                            <MdCalendarMonth className="text-2xl" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-navy-700 dark:text-white flex items-center gap-2">
                                Çalışma Şablonu Oluşturucu
                                <button onClick={() => setIsInfoOpen(true)} className="text-gray-400 hover:text-brand-500 transition-colors" title="Nasıl Çalışır?">
                                    <MdInfoOutline className="text-lg" />
                                </button>
                            </h2>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Birden fazla personel ve gün için tek seferde planlama yapın.</p>
                        </div>
                    </div>
                </div>

                {/* Mesaj Bildirimleri */}
                {message.text && (
                    <div className={`mx-6 mt-6 p-4 rounded-xl flex items-center gap-3 border ${
                        message.type === 'success' ? 'bg-green-50 border-green-200 text-green-600 dark:bg-green-500/10 dark:border-green-500/20 dark:text-green-400' :
                            message.type === 'danger' ? 'bg-red-50 border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400' :
                                'bg-amber-50 border-amber-200 text-amber-600 dark:bg-amber-500/10 dark:border-amber-500/20 dark:text-amber-400'
                    }`}>
                        {message.type === 'success' ? <MdCheckCircle className="text-xl flex-none"/> : <MdWarning className="text-xl flex-none"/>}
                        <span className="text-sm font-bold">{message.text}</span>
                    </div>
                )}

                {/* Form İçeriği (3 Kolonlu Grid) */}
                <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">

                    {/* A. ZAMAN AYARLARI */}
                    <div className="flex flex-col p-5 rounded-2xl border border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-navy-900/50">
                        <h6 className="text-base font-bold text-navy-700 dark:text-white flex items-center gap-2 mb-4">
                            <BsClock className="text-brand-500" /> Zaman Ayarları
                        </h6>
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1">Başlangıç Saati</label>
                                <div className="relative mt-1">
                                    <select value={startTime} onChange={e => setStartTime(e.target.value)} className="w-full h-11 appearance-none rounded-xl border border-gray-200 bg-white px-4 text-sm font-bold text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white cursor-pointer transition-all">
                                        {timeOptions.map(t => <option key={`s-${t}`} value={t}>{t}</option>)}
                                    </select>
                                    <MdKeyboardArrowDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1">Bitiş Saati</label>
                                <div className="relative mt-1">
                                    <select value={endTime} onChange={e => setEndTime(e.target.value)} className="w-full h-11 appearance-none rounded-xl border border-gray-200 bg-white px-4 text-sm font-bold text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white cursor-pointer transition-all">
                                        {timeOptions.map(t => <option key={`e-${t}`} value={t}>{t}</option>)}
                                    </select>
                                    <MdKeyboardArrowDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1">Randevu Aralığı</label>
                                <div className="relative mt-1">
                                    <select value={slotDuration} onChange={e => setSlotDuration(parseInt(e.target.value))} className="w-full h-11 appearance-none rounded-xl border border-gray-200 bg-white px-4 text-sm font-bold text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white cursor-pointer transition-all">
                                        {slotDurations.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                                    </select>
                                    <MdKeyboardArrowDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* B. GÜNLER (Pills) */}
                    <div className="flex flex-col p-5 rounded-2xl border border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-navy-900/50">
                        <div className="flex items-center justify-between mb-4">
                            <h6 className="text-base font-bold text-navy-700 dark:text-white flex items-center gap-2">
                                <MdCalendarMonth className="text-amber-500" /> Gün Seçimi
                            </h6>
                            <button onClick={toggleAllDays} className="text-xs font-bold text-amber-500 hover:text-amber-600 transition-colors bg-amber-50 dark:bg-amber-500/10 px-2 py-1 rounded">
                                Hafta İçi
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {daysOfWeek.map(day => {
                                const isSelected = selectedDays.includes(day.value);
                                return (
                                    <button
                                        key={day.value}
                                        onClick={() => toggleDay(day.value)}
                                        className={`flex-1 min-w-[70px] py-2 rounded-xl text-sm font-bold transition-all border ${
                                            isSelected
                                                ? 'bg-amber-500 border-amber-500 text-white shadow-md shadow-amber-500/20'
                                                : 'bg-white border-gray-200 text-gray-500 hover:border-amber-500 hover:text-amber-500 dark:bg-navy-800 dark:border-white/10 dark:text-gray-300 dark:hover:border-amber-500'
                                        }`}
                                    >
                                        {day.short}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-auto pt-4 flex justify-between items-center text-sm font-bold text-gray-500">
                            <span>Seçili Gün:</span>
                            <span className="text-navy-700 dark:text-white bg-white dark:bg-navy-800 px-3 py-1 rounded-lg border border-gray-200 dark:border-white/10">{selectedDays.length}</span>
                        </div>
                    </div>

                    {/* C. PERSONEL (Custom Checklist) */}
                    <div className="flex flex-col p-5 rounded-2xl border border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-navy-900/50 h-full max-h-[350px]">
                        <div className="flex items-center justify-between mb-4">
                            <h6 className="text-base font-bold text-navy-700 dark:text-white flex items-center gap-2">
                                <MdPerson className="text-green-500" /> Personel
                            </h6>
                            <button onClick={toggleAllStaff} className="text-xs font-bold text-green-500 hover:text-green-600 transition-colors bg-green-50 dark:bg-green-500/10 px-2 py-1 rounded">
                                {selectedStaffIds.length === staffList.length ? 'Temizle' : 'Tümü'}
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto custom-sidebar-scroll pr-2 space-y-2">
                            {staffList.map(staff => {
                                const isSelected = selectedStaffIds.includes(staff.id);
                                return (
                                    <div
                                        key={staff.id}
                                        onClick={() => toggleStaff(staff.id)}
                                        className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all border ${
                                            isSelected
                                                ? 'bg-green-50 border-green-500 dark:bg-green-500/10 dark:border-green-500'
                                                : 'bg-white border-gray-100 hover:border-green-300 dark:bg-navy-800 dark:border-white/5 dark:hover:border-green-500/50'
                                        }`}
                                    >
                    <span className={`text-sm font-bold ${isSelected ? 'text-green-700 dark:text-green-400' : 'text-navy-700 dark:text-white'}`}>
                      {staff.name} {staff.surname}
                    </span>
                                        <div className={`w-5 h-5 rounded flex items-center justify-center border transition-colors ${
                                            isSelected ? 'bg-green-500 border-green-500 text-white' : 'bg-transparent border-gray-300 dark:border-gray-500'
                                        }`}>
                                            {isSelected && <MdCheckCircle className="text-sm" />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                </div>

                {/* Toplu İşlem Butonu */}
                <div className="p-6 border-t border-gray-100 dark:border-white/10 bg-gray-50/30 dark:bg-navy-900/30 flex justify-end">
                    <button
                        onClick={handleBulkCreate}
                        disabled={loading || selectedStaffIds.length === 0 || selectedDays.length === 0}
                        className="flex items-center justify-center gap-2 w-full sm:w-auto px-8 h-12 rounded-xl bg-brand-500 text-white font-bold text-sm shadow-lg shadow-brand-500/30 hover:bg-brand-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all active:scale-95"
                    >
                        {loading ? <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><MdAdd className="text-xl" /> Şablonları Oluştur</>}
                    </button>
                </div>
            </div>

            {/* 2. BÖLÜM: MEVCUT ŞABLONLAR (Gelişmiş Akordeon Yapısı) */}
            <div>
                <h4 className="text-lg font-bold text-navy-700 dark:text-white flex items-center gap-2 mb-4 px-1">
                    Mevcut Şablonlar & Düzenleme
                </h4>

                <div className="space-y-3">
                    {currentStaffList.map(staff => {
                        const isExpanded = expandedStaffId === staff.id;
                        return (
                            <div key={staff.id} className="bg-white dark:bg-navy-800 rounded-2xl shadow-sm border border-gray-100 dark:border-white/10 overflow-hidden transition-all">

                                {/* Accordion Başlık */}
                                <div
                                    onClick={() => setExpandedStaffId(isExpanded ? null : staff.id)}
                                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-900/50 transition-colors"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="h-10 w-10 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold shadow-md">
                                            {staff.name[0]}{staff.surname[0]}
                                        </div>
                                        <div>
                                            <h5 className="text-base font-bold text-navy-700 dark:text-white leading-tight">{staff.name} {staff.surname}</h5>
                                            <p className="text-xs font-bold text-gray-500 uppercase mt-0.5">{staff.role === 'admin' ? 'Yönetici' : 'Personel'}</p>
                                        </div>
                                    </div>
                                    <div className={`p-2 rounded-full bg-gray-100 dark:bg-navy-900 text-gray-500 transition-transform duration-300 ${isExpanded ? 'rotate-180 text-brand-500' : ''}`}>
                                        <MdExpandMore className="text-xl" />
                                    </div>
                                </div>

                                {/* Accordion İçerik (Work Days) */}
                                <div className={`transition-all duration-300 ease-in-out origin-top ${isExpanded ? 'max-h-[1000px] opacity-100 border-t border-gray-100 dark:border-white/10' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                                    {loadingWorkDays && isExpanded ? (
                                        <WorkDaysSkeleton />
                                    ) : (
                                        <div className="p-4 bg-gray-50/50 dark:bg-navy-900/20">
                                            {staffWorkDays.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center py-6 text-gray-400 border border-dashed border-gray-300 dark:border-white/10 rounded-2xl">
                                                    <MdWarning className="text-4xl mb-2 opacity-50" />
                                                    <p className="text-sm font-bold">Aktif çalışma şablonu bulunmuyor.</p>
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                    {staffWorkDays.map(wd => (
                                                        <div key={wd.id} className="relative flex flex-col justify-between p-4 bg-white dark:bg-navy-800 rounded-2xl border border-gray-100 dark:border-white/10 shadow-sm hover:shadow-md transition-shadow">
                                                            <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-brand-500" />
                                                            <div className="flex items-center justify-between mb-3 pl-2">
                                                                <h6 className="text-sm font-bold text-navy-700 dark:text-white">{wd.day_of_week}</h6>
                                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${wd.is_active ? 'bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-navy-900 dark:text-gray-400'}`}>
                                  {wd.is_active ? 'Aktif' : 'Pasif'}
                                </span>
                                                            </div>
                                                            <div className="flex items-end justify-between pl-2">
                                                                <div>
                                                                    <p className="text-lg font-black text-brand-500 dark:text-white leading-none font-mono">
                                                                        {wd.start_time.substring(0,5)} - {wd.end_time.substring(0,5)}
                                                                    </p>
                                                                    <p className="text-[10px] font-bold text-gray-400 uppercase mt-1 tracking-wider">{wd.slot_duration} Dk Aralık</p>
                                                                </div>
                                                                <button
                                                                    onClick={() => handleDeleteWorkDay(wd.id)}
                                                                    disabled={deletingId === wd.id}
                                                                    className="h-8 w-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-500 hover:text-white transition-all disabled:opacity-50 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500 dark:hover:text-white"
                                                                    title="Şablonu Sil"
                                                                >
                                                                    {deletingId === wd.id ? <div className="h-3 w-3 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <MdDelete className="text-sm" />}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* SAYFALAMA (Pagination) */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6">
                        <p className="text-sm font-medium text-gray-500">
                            Sayfa <span className="font-bold text-navy-700 dark:text-white">{currentPage}</span> / {totalPages}
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-bold text-navy-700 disabled:opacity-40 hover:bg-gray-50 transition-all dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                            >
                                Geri
                            </button>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-bold text-navy-700 disabled:opacity-40 hover:bg-gray-50 transition-all dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                            >
                                İleri
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* CSS Injection for Custom Scrollbar */}
            <style jsx="true">{`
        .custom-sidebar-scroll::-webkit-scrollbar { width: 5px; }
        .custom-sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .custom-sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(163, 174, 208, 0.4); border-radius: 10px; }
        .dark .custom-sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.2); }
      `}</style>
        </div>
    );
};

export default AdminScheduleManager;